import { useId } from 'react'
import { toast } from 'sonner'
import { Input, Label } from '@teach/ui'
import type { CourseWithoutBlocks, CourseUpdate } from '@/types/course'
import { toNumber, PRICE_RUB_MIN, PRICE_USD_MIN } from '../../lib/coursePatch'
import { useMessages } from '@/i18n'
import { messages } from './messages'

type Currency = 'RUB' | 'USD'

interface Props {
  local: CourseWithoutBlocks
  setLocal: (next: CourseWithoutBlocks) => void
  save: (patch: CourseUpdate) => void
  isCourseAuthor: boolean
  /** Ноль допустим только у бесплатного курса — см. priceThresholdError. */
  zeroAllowed: boolean
}

interface FieldProps extends Props {
  currency: Currency
}

// Одна колонка цены. У курса две независимые цены (`priceRub` и `usdPrice`), и ноль
// у любой из них значит «бесплатно» для покупателей этой валюты, поэтому обе видны
// сразу и обе проверяются порогом платёжной системы — скрытой второй валюты нет.
const PriceField = ({
  currency,
  local,
  setLocal,
  save,
  isCourseAuthor,
  zeroAllowed,
}: FieldProps) => {
  const m = useMessages(messages)
  const id = useId()
  const isRub = currency === 'RUB'
  const value = toNumber(isRub ? local.priceRub : local.usdPrice)
  const min = isRub ? PRICE_RUB_MIN : PRICE_USD_MIN
  const raw = (isRub ? local.priceRub : local.usdPrice) ?? ''
  const label = isRub ? m.priceRub : m.priceUsd
  const minText = isRub ? `${min} ₽` : `$${min.toFixed(2)}`

  const change = (next: string) => {
    const parsed = next === '' ? null : Number(next)
    setLocal(isRub ? { ...local, priceRub: parsed } : { ...local, usdPrice: parsed })
  }

  return (
    <div className="min-w-0 flex-1 space-y-2">
      <Label htmlFor={id} className="!text-base">
        {label}
      </Label>
      <div className="relative">
        <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-muted-foreground">
          {isRub ? '₽' : '$'}
        </span>
        <Input
          id={id}
          type="number"
          step={isRub ? '1' : '0.01'}
          value={raw}
          onChange={(e) => change(e.target.value)}
          onBlur={() => {
            // Дешевле минимума платёжной системы не бывает: вместо красной ошибки поле
            // само встаёт на минимум и сохраняется. Ноль допустим только там, где курс
            // не продаётся отдельно (zeroAllowed) — в «Отдельно» его нет.
            const next = zeroAllowed && value === 0 ? 0 : Math.max(min, value)
            if (next !== value) {
              setLocal(isRub ? { ...local, priceRub: next } : { ...local, usdPrice: next })
              // Пустое поле — не «ниже минимума», а ещё не введённая цена: молчим.
              if (raw !== '') toast.info(m.priceRaised(minText))
            }
            save(isRub ? { priceRub: next } : { usdPrice: next })
          }}
          disabled={!isCourseAuthor}
          title={!isCourseAuthor ? m.noRights : undefined}
          min={min}
          className="h-12 pl-10 text-lg"
        />
      </div>
    </div>
  )
}

export const PriceInputs = (props: Props) => {
  const m = useMessages(messages)
  return (
    <div className="space-y-4 rounded-2xl bg-foreground/5 p-5">
      <h3 className="text-lg font-medium leading-none">{m.priceForeverTitle}</h3>
      <div className="flex flex-col gap-4 sm:flex-row">
        <PriceField currency="RUB" {...props} />
        <PriceField currency="USD" {...props} />
      </div>
    </div>
  )
}
